import { useEffect, useRef, useState, useCallback } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { QrCode, Download, Share2, Save, Check, AlertTriangle, ChevronDown, FileImage, FileCode, Printer } from 'lucide-react';
import { useQR } from '@/hooks/useQRContext';
import { useToast } from '@/components/common/Toast';
import { cn, copyToClipboard } from '@/utils/helpers';
import Button from '@/components/common/Button';

/**
 * Converts gradient config to qr-code-styling format
 */
function convertGradient(gradient) {
  if (!gradient) return undefined;
  return {
    type: gradient.type || 'linear',
    rotation: gradient.rotation || 0,
    colorStops: gradient.colors.map(stop => ({
      offset: stop.offset,
      color: stop.color
    }))
  };
}

/**
 * Calculate color contrast ratio
 */
function getContrastRatio(color1, color2) {
  const getLuminance = (hex) => {
    const rgb = parseInt(hex.replace('#', ''), 16);
    const r = ((rgb >> 16) & 255) / 255;
    const g = ((rgb >> 8) & 255) / 255;
    const b = (rgb & 255) / 255;
    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  };
  
  try {
    const l1 = getLuminance(color1);
    const l2 = getLuminance(color2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  } catch {
    return 21;
  }
}

/**
 * Assess QR code scannability
 */
function assessScannability(config) {
  const warnings = [];
  const suggestions = [];
  
  // Check contrast
  const fgColor = config.dotsColor || '#000000';
  const bgColor = config.transparentBackground ? '#ffffff' : (config.backgroundColor || '#ffffff');
  const contrast = getContrastRatio(fgColor, bgColor);
  
  if (contrast < 3) {
    warnings.push('Very low contrast');
    suggestions.push('Increase contrast between QR code and background colors for reliable scanning');
  } else if (contrast < 4) {
    warnings.push('Low contrast');
    suggestions.push('Consider using darker foreground or lighter background colors');
  }
  
  // Check margin
  if (config.margin < 4) {
    warnings.push('Small quiet zone');
    suggestions.push('Add at least 10px margin for reliable scanning');
  }
  
  // Check logo size
  if (config.image && config.imageSize > 0.35) {
    warnings.push('Large logo');
    suggestions.push('Reduce logo size to under 30% for better scan reliability');
  }
  
  // Check error correction with logo
  if (config.image && config.errorCorrectionLevel !== 'H') {
    warnings.push('Logo without high error correction');
    suggestions.push('Use High error correction level when adding a logo');
  }

  // Determine quality
  let quality = 'excellent';
  if (warnings.length === 1) quality = 'good';
  if (warnings.length === 2) quality = 'fair';
  if (warnings.length >= 3 || contrast < 3) quality = 'poor';
  
  return { quality, warnings, suggestions, contrast };
}

/**
 * QR Code Preview component
 * Renders the QR code with real-time updates and provides download/share options
 */
function QRPreview({ onSave }) {
  const { qrConfig, getShareableLink } = useQR();
  const { success, error } = useToast();
  
  const containerRef = useRef(null);
  const qrCodeRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [scannability, setScannability] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Create/update QR code instance when config changes
  useEffect(() => {
    if (!qrConfig.data) {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      qrCodeRef.current = null;
      setScannability(null);
      return;
    }

    setIsGenerating(true);

    // Map config to qr-code-styling options
    const options = {
      width: qrConfig.width || 300,
      height: qrConfig.height || 300,
      data: qrConfig.data,
      margin: qrConfig.margin || 10,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: qrConfig.errorCorrectionLevel || 'Q',
      },
      dotsOptions: {
        type: qrConfig.dotsType || 'rounded',
        color: qrConfig.dotsColor || '#000000',
      },
      cornersSquareOptions: {
        type: qrConfig.cornersSquareType || 'extra-rounded',
        color: qrConfig.cornersSquareColor || '#000000',
      },
      cornersDotOptions: {
        type: qrConfig.cornersDotType || 'dot',
        color: qrConfig.cornersDotColor || '#000000',
      },
      backgroundOptions: {
        color: qrConfig.transparentBackground ? 'transparent' : (qrConfig.backgroundColor || '#ffffff'),
      },
    };
    
    // Apply gradients
    if (qrConfig.dotsGradient) {
      options.dotsOptions.gradient = convertGradient(qrConfig.dotsGradient);
    }
    if (qrConfig.cornersSquareGradient) {
      options.cornersSquareOptions.gradient = convertGradient(qrConfig.cornersSquareGradient);
    }
    if (qrConfig.cornersDotGradient) {
      options.cornersDotOptions.gradient = convertGradient(qrConfig.cornersDotGradient);
    }
    if (qrConfig.backgroundGradient && !qrConfig.transparentBackground) {
      options.backgroundOptions.gradient = convertGradient(qrConfig.backgroundGradient);
    }
    
    // Handle shape
    if (qrConfig.shapeType === 'circle') {
      options.shape = 'circle';
    }

    // Add logo if provided
    if (qrConfig.image) {
      options.image = qrConfig.image;
      options.imageOptions = {
        crossOrigin: 'anonymous',
        margin: qrConfig.imageMargin || 5,
        imageSize: qrConfig.imageSize || 0.4,
        hideBackgroundDots: true,
      };
    }

    // Create or update QR code
    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling(options);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        qrCodeRef.current.append(containerRef.current);
      }
    } else {
      qrCodeRef.current.update(options);
    }

    // Assess scannability
    const assessment = assessScannability(qrConfig);
    setScannability(assessment);

    setIsGenerating(false);
  }, [qrConfig]);

  // Download as PNG (supports transparency)
  const handleDownloadPNG = useCallback(async () => {
    if (!qrCodeRef.current) return;
    
    try {
      await qrCodeRef.current.download({
        name: `qr-code-${Date.now()}`,
        extension: 'png',
      });
      success('QR code downloaded as PNG' + (qrConfig.transparentBackground ? ' (with transparent background)' : ''));
    } catch (err) {
      error('Failed to download QR code');
    }
  }, [success, error, qrConfig.transparentBackground]);

  // Download as SVG (supports transparency)
  const handleDownloadSVG = useCallback(async () => {
    if (!qrCodeRef.current) return;
    
    try {
      await qrCodeRef.current.download({
        name: `qr-code-${Date.now()}`,
        extension: 'svg',
      });
      success('QR code downloaded as SVG' + (qrConfig.transparentBackground ? ' (with transparent background)' : ''));
    } catch (err) {
      error('Failed to download QR code');
    }
  }, [success, error, qrConfig.transparentBackground]);

  // Download high-res for print
  const handleDownloadPrint = useCallback(async (dpi = 300) => {
    if (!qrCodeRef.current) return;
    
    try {
      const printSize = Math.round((dpi / 72) * qrConfig.width);
      const printOptions = {
        ...qrCodeRef.current._options,
        width: Math.min(printSize, 2000),
        height: Math.min(printSize, 2000),
      };
      
      const printQR = new QRCodeStyling(printOptions);
      await printQR.download({
        name: `qr-code-print-${dpi}dpi-${Date.now()}`,
        extension: 'png',
      });
      success(`High-resolution QR code downloaded (${dpi} DPI)`);
    } catch (err) {
      error('Failed to download print version');
    }
  }, [qrConfig.width, success, error]);

  // Share link
  const handleShare = useCallback(async () => {
    const link = getShareableLink();
    const copied = await copyToClipboard(link);
    
    if (copied) {
      success('Shareable link copied to clipboard!');
    } else {
      error('Failed to copy link');
    }
  }, [getShareableLink, success, error]);

  // Scannability indicator
  const getScannabilityIndicator = () => {
    if (!scannability) return null;

    const colors = {
      excellent: 'text-green-500',
      good: 'text-green-400',
      fair: 'text-yellow-500',
      poor: 'text-red-500',
    };

    const bgColors = {
      excellent: 'bg-green-50 dark:bg-green-900/20',
      good: 'bg-green-50 dark:bg-green-900/20',
      fair: 'bg-yellow-50 dark:bg-yellow-900/20',
      poor: 'bg-red-50 dark:bg-red-900/20',
    };

    const labels = {
      excellent: 'Excellent',
      good: 'Good',
      fair: 'Fair - May have issues',
      poor: 'Poor - Likely to fail',
    };

    return (
      <div className={cn('p-3 rounded-lg', bgColors[scannability.quality])}>
        <div className={cn('flex items-center gap-2 text-sm font-medium', colors[scannability.quality])}>
          {scannability.quality === 'poor' || scannability.quality === 'fair' ? (
            <AlertTriangle className="w-4 h-4" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          <span>Scan Quality: {labels[scannability.quality]}</span>
        </div>
        {scannability.suggestions.length > 0 && (
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
            {scannability.suggestions[0]}
          </p>
        )}
      </div>
    );
  };

  const hasQR = Boolean(qrConfig.data);

  return (
    <div className="flex flex-col h-full">
      {/* Preview Container */}
      <div 
        className={cn(
          'flex-1 flex items-center justify-center p-6 rounded-xl',
          'bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-700',
          hasQR && 'border-solid bg-white dark:bg-gray-800'
        )}
        style={hasQR && qrConfig.transparentBackground ? {
          backgroundImage: 'linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
        } : {}}
      >
        {!hasQR ? (
          <div className="text-center text-gray-400">
            <QrCode className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Your QR code will appear here</p>
            <p className="text-sm mt-1">Enter a URL or text to get started</p>
          </div>
        ) : isGenerating ? (
          <div className="flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div 
            ref={containerRef} 
            className="transition-all duration-300"
          />
        )}
      </div>

      {/* Transparent Background Indicator */}
      {hasQR && qrConfig.transparentBackground && (
        <div className="mt-2 text-center">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Transparent background enabled (checkerboard shows transparency)
          </span>
        </div>
      )}

      {/* Scannability Indicator */}
      {hasQR && scannability && (
        <div className="mt-4">
          {getScannabilityIndicator()}
        </div>
      )}

      {/* Action Buttons */}
      {hasQR && (
        <div className="mt-4 flex flex-col gap-2">
          {/* Download dropdown */}
          <div className="relative">
            <Button
              variant="primary"
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              icon={Download}
              className="w-full justify-between"
            >
              Download
              <ChevronDown className={cn("w-4 h-4 transition-transform", showDownloadMenu && "rotate-180")} />
            </Button>
            
            {showDownloadMenu && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden z-10">
                <button
                  onClick={() => { handleDownloadPNG(); setShowDownloadMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <FileImage className="w-4 h-4 text-primary-500" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      PNG Image {qrConfig.transparentBackground && '(Transparent)'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Best for web & digital use</div>
                  </div>
                </button>
                <button
                  onClick={() => { handleDownloadSVG(); setShowDownloadMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-t border-gray-100 dark:border-slate-700"
                >
                  <FileCode className="w-4 h-4 text-secondary-500" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      SVG Vector {qrConfig.transparentBackground && '(Transparent)'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Scalable, ideal for design tools</div>
                  </div>
                </button>
                <button
                  onClick={() => { handleDownloadPrint(300); setShowDownloadMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-t border-gray-100 dark:border-slate-700"
                >
                  <Printer className="w-4 h-4 text-green-500" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">Print-Ready (300 DPI)</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">High resolution for printing</div>
                  </div>
                </button>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              onClick={handleShare}
              icon={Share2}
            >
              Share
            </Button>
            <Button
              variant="secondary"
              onClick={onSave}
              icon={Save}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default QRPreview;
